import React, { useEffect, useState } from 'react';
import { useAppContext } from '../../context/AppContext';
import { Task } from 'shared/types';
import { i18n } from '../../i18n/i18n';
import { useLanguage } from '../../context/LanguageContext';
import { getRoomName } from '../../utils/roomName';
import { showToast } from '../kid/Toast';
import { isAvailableNow } from '../../utils/taskAvailability';
import { leaderTitle, titleOfSeries } from '../../utils/taskChains';
import { activeCards } from '../../utils/sharedTasks';
import HeaderImage from './HeaderImage';
import TaskLibrary from './TaskLibrary';
import TaskFormModal from './TaskFormModal';

/** Admin → Tasks: task library, active and finished tasks, task form (split out of AdminPanel.tsx, 2026-10-12). */
const TasksTab: React.FC = () => {
  const { current_user, current_family, deleteTask, loadMoreCompletedTasks } = useAppContext() as any;
  const { current_language } = useLanguage();
  const [editTaskModal, setEditTaskModal] = useState<{ isOpen: boolean; task: Task | null; isCreating: boolean }>({
    isOpen: false,
    task: null,
    isCreating: false
  });
  const [removeTaskModal, setRemoveTaskModal] = useState<{ isOpen: boolean; taskId: string; taskTitle: string; wholeGroup?: boolean }>({
    isOpen: false,
    taskId: '',
    taskTitle: ''
  });
  // Completed Tasks list is collapsed to the 5 most recent by default; "Show more" reveals
  // 10 more at a time so a family with hundreds of completed tasks doesn't render them all at once.
  const COMPLETED_TASKS_INITIAL_COUNT = 5;
  const COMPLETED_TASKS_PAGE_SIZE = 10;
  const [completedTasksShown, setCompletedTasksShown] = useState(COMPLETED_TASKS_INITIAL_COUNT);
  // A task just added from the library: scroll to it and outline it for a moment.
  const [justAddedId, setJustAddedId] = useState<string | null>(null);
  const onTaskAdded = (taskId: string, title: string) => {
    setJustAddedId(taskId);
    showToast({ message: i18n.t('library.added_to_tasks').replace('{{title}}', title) });
  };
  useEffect(() => {
    if (!justAddedId) return;
    const el = document.getElementById(`task-card-${justAddedId}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const t = window.setTimeout(() => setJustAddedId(null), 3500);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [justAddedId, current_family?.tasks?.length]);
  const [loadingOlderCompleted, setLoadingOlderCompleted] = useState(false);
  const handleRemoveTask = async () => {
    const id = removeTaskModal.taskId;
    if (!id) return;
    try {
      await deleteTask(id, { wholeGroup: removeTaskModal.wholeGroup });
      setRemoveTaskModal({ isOpen: false, taskId: '', taskTitle: '' });
    } catch (error) {
      console.error('Failed to remove task:', error);
      showToast({ message: (error as Error)?.message || i18n.t('admin_panel.err_task_remove'), tone: 'error' });
    }
  };

  const cancelRemoveTask = () => {
    setRemoveTaskModal({ isOpen: false, taskId: '', taskTitle: '' });
  };

  // Checklist handlers
  if (!current_family || !current_user) return null;

    // Filter tasks into active and completed
    const activeTasks = current_family.tasks.filter((task: any) => !(task as any).is_completed);
    const completedTaskSortTime = (task: any) => {
      const raw = task.completed_at || task.verified_at || task.updated_at;
      const t = raw ? new Date(raw).getTime() : NaN;
      return Number.isNaN(t) ? 0 : t;
    };
    const completedTasks = current_family.tasks
      .filter((task: any) => (task as any).is_completed)
      .sort((a: any, b: any) => completedTaskSortTime(b) - completedTaskSortTime(a));
    const visibleCompletedTasks = completedTasks.slice(0, completedTasksShown);
    // get-family only sends the most recent finished tasks; older ones are fetched on demand.
    const hasMoreCompletedTasks = completedTasksShown < completedTasks.length || !!current_family.completed_has_more;
    const awaitingApprovalCount = completedTasks.filter((t: any) => !t.verified).length;
    const completedTasksTotal = current_family.completed_total != null
      ? awaitingApprovalCount + current_family.completed_total
      : completedTasks.length;
    const hasCollapsedCompletedTasks = completedTasksShown > COMPLETED_TASKS_INITIAL_COUNT;

    return (
      <>
      <div className="mb-8">
        <div className="flex flex-wrap justify-between items-center gap-3 mb-5">
          <h3 className="font-kid-heading text-2xl font-extrabold text-kid-text dark:text-kid-text-dark">{i18n.t('admin_panel.task_management')}</h3>
          <button 
            className="inline-flex items-center justify-center gap-2 min-h-[48px] px-5 rounded-full font-bold text-base bg-kid-green hover:bg-kid-green-hover text-white shadow-[0_3px_0_0_#1F5C39] disabled:opacity-50"
            onClick={() => {
              setEditTaskModal({ isOpen: true, task: null, isCreating: true });
            }}
          >
            + {i18n.t('admin_panel.create_new_task')}
          </button>
        </div>

        {/* Task library: standard tasks (folded away by default) + the family's saved templates */}
        <TaskLibrary onTaskAdded={onTaskAdded} />

        {/* Active Tasks Section */}
        <div className="mb-8">
          <div className="mb-4">
            <h4 className="font-kid-heading text-xl font-extrabold text-kid-text dark:text-kid-text-dark mb-2">
              {i18n.t('admin_panel.active_tasks')} ({activeTasks.length})
            </h4>
            <p className="text-kid-text dark:text-kid-text-dark text-sm">{i18n.t('admin_panel.active_tasks_help')}</p>
          </div>
          
          {activeTasks.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {activeCards(activeTasks as any[], current_family.tasks as any[]).map((card: any) => { const task = card.task; return (
                <div key={card.kind === 'group' ? card.groupId : task.id} id={`task-card-${task.id}`} className={`bg-dashboard-card dark:bg-dashboard-card-dark rounded-kid-card border-2 overflow-hidden scroll-mt-24 transition-shadow duration-500 ${justAddedId === task.id ? 'border-kid-green ring-4 ring-kid-green/40' : 'border-dashboard-border dark:border-dashboard-border-dark'}`}>
                  {/* Header Image */}
                  {(task as any).header_image_url || (task as any).header_image ? (
                    <div className="w-full aspect-[16/9] overflow-hidden bg-kid-chip dark:bg-kid-chip-dark">
                      {(task as any).header_image_url ? (
                        <img
                          src={(task as any).header_image_url}
                          alt=""
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <HeaderImage imageId={(task as any).header_image} />
                      )}
                    </div>
                  ) : null}
                  <div className="p-6">
                    <div className="mb-4">
                      <h4 className="text-lg font-semibold text-kid-text dark:text-kid-text-dark mb-2">{task.title}</h4>
                      <p className="text-kid-text dark:text-kid-text-dark">{i18n.t('task_details.room')}: {getRoomName(current_family.rooms, task.room_id) || i18n.t('admin_panel.no_room_option')}</p>
                      <p className="text-kid-text dark:text-kid-text-dark">{i18n.t('task_details.points')}: {task.points}</p>
                      {card.kind === 'group' ? (
                        // A shared task: everyone it is for, with how far their own copy is (2026-10-14).
                        <ul className="mt-1 space-y-1">
                          {card.copies.map((c: any) => (
                            <li key={c.id} className="flex flex-wrap items-center gap-2 text-kid-text dark:text-kid-text-dark">
                              <span className="font-bold">{current_family.members.find((m: any) => m.id === c.assigned_to)?.name || '…'}</span>
                              <span className="rounded-full bg-kid-chip dark:bg-kid-chip-dark px-3 py-0.5 text-sm">
                                {!isAvailableNow(c) && c.available_from
                                  ? '🔁 ' + i18n.t('admin_panel.comes_back_on').replace('{{date}}', new Date(c.available_from).toLocaleDateString(current_language, { weekday: 'short', day: 'numeric', month: 'short' }))
                                  : i18n.t('admin_panel.shared_ready')}
                              </span>
                            </li>
                          ))}
                        </ul>
                      ) : (
                      <p className="text-kid-text dark:text-kid-text-dark">
                        {(task as any).assigned_to
                          ? i18n.t('admin_panel.status_assigned').replace('{{name}}', current_family.members.find((m: any) => m.id === (task as any).assigned_to)?.name || '')
                          : i18n.t('admin_panel.status_open')}
                      </p>
                      )}
                      {card.kind === 'group' ? null : (task as any).is_waiting ? (
                        <p className="mt-1 inline-flex items-center gap-1 rounded-full bg-kid-chip dark:bg-kid-chip-dark px-3 py-1 text-sm font-bold">
                          ⏳ {i18n.t('admin_panel.waits_for').replace('{{task}}', leaderTitle(current_family.tasks as any, task as any) || '…')}
                        </p>
                      ) : !isAvailableNow(task) && (
                        <p className="mt-1 inline-flex items-center gap-1 rounded-full bg-kid-chip dark:bg-kid-chip-dark px-3 py-1 text-sm font-bold">
                          🔁 {i18n.t('admin_panel.comes_back_on').replace('{{date}}', new Date((task as any).available_from).toLocaleDateString(current_language, { weekday: 'short', day: 'numeric', month: 'short' }))}
                        </p>
                      )}
                      {(task as any).next_series_id && titleOfSeries(current_family.tasks as any, (task as any).next_series_id) && (
                        <p className="mt-1 text-sm font-bold text-kid-text-muted dark:text-kid-text-muted-dark">
                          ➜ {i18n.t('admin_panel.then_next').replace('{{task}}', titleOfSeries(current_family.tasks as any, (task as any).next_series_id) || '')}
                        </p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <button 
                        className="inline-flex items-center justify-center gap-2 min-h-[48px] px-5 rounded-full font-bold text-base bg-kid-card dark:bg-kid-card-dark border-2 border-kid-border-strong dark:border-kid-border-strong-dark text-kid-text dark:text-kid-text-dark disabled:opacity-50"
                        onClick={() => {
                          setEditTaskModal({ isOpen: true, task, isCreating: false });
                        }}
                      >
                        {i18n.t('admin_panel.edit')}
                      </button>
                      <button 
                        className="inline-flex items-center justify-center gap-2 min-h-[48px] px-5 rounded-full font-bold text-base bg-transparent border-2 border-red-300 dark:border-red-800 text-red-800 dark:text-red-200 disabled:opacity-50"
                        onClick={() => setRemoveTaskModal({ 
                          isOpen: true, 
                          taskId: task.id, 
                          taskTitle: task.title,
                          wholeGroup: card.kind === 'group',
                        })}
                      >
                        {i18n.t('admin_panel.remove')}
                      </button>
                    </div>
                  </div>
                </div>
              ); })}
            </div>
          ) : (
            <div className="text-center py-8 text-kid-text-muted">
              <p>{i18n.t('admin_panel.no_active_tasks')}</p>
            </div>
          )}
        </div>

        {/* Completed Tasks Section */}
        <div className="mb-8">
          <div className="mb-4">
            <h4 className="font-kid-heading text-xl font-extrabold text-kid-text dark:text-kid-text-dark mb-2">
              {i18n.t('admin_panel.completed_tasks')} ({completedTasksTotal})
            </h4>
            <p className="text-kid-text dark:text-kid-text-dark text-sm">{i18n.t('admin_panel.completed_tasks_help')}</p>
          </div>
          
          {completedTasks.length > 0 ? (
            // Collapsed, single-row cards (matches the compact card pattern on /tasks) instead of
            // the full detail cards Active Tasks uses above — completed tasks don't need the same
            // amount of on-screen space once they're done.
            <div className="flex flex-col gap-3">
              {visibleCompletedTasks.map((task: any) => (
                <div key={task.id} className="bg-dashboard-card dark:bg-dashboard-card-dark rounded-kid-card border-2 border-dashboard-border dark:border-dashboard-border-dark p-4">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-kid-card bg-kid-chip dark:bg-kid-chip-dark shrink-0 overflow-hidden flex items-center justify-center">
                      {(task as any).header_image_url ? (
                        <img src={(task as any).header_image_url} alt="" className="w-full h-full object-cover" />
                      ) : (task as any).header_image ? (
                        <div className="w-full h-full [&_img]:w-full [&_img]:h-full [&_img]:object-cover">
                          <HeaderImage imageId={(task as any).header_image} />
                        </div>
                      ) : (
                        <span className="text-xl" aria-hidden>✅</span>
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <h4 className="text-base font-semibold text-kid-text dark:text-kid-text-dark truncate">{task.title}</h4>
                      <div className="flex flex-wrap items-center gap-2 mt-1.5">
                        <span className="inline-flex items-center gap-1 bg-kid-chip dark:bg-kid-chip-dark text-kid-text dark:text-kid-text-dark text-xs font-medium px-2.5 py-1 rounded-full">
                          🏠 {getRoomName(current_family.rooms, task.room_id) || i18n.t('admin_panel.no_room_option')}
                        </span>
                        <span className="inline-flex items-center gap-1 bg-amber-50 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200 text-xs font-semibold px-2.5 py-1 rounded-full">
                          ⭐ {task.points}
                        </span>
                        {(task as any).assigned_to && (
                          <span className="inline-flex items-center gap-1 bg-kid-chip dark:bg-kid-chip-dark text-kid-text dark:text-kid-text-dark text-xs font-medium px-2.5 py-1 rounded-full">
                            ✅ {current_family.members.find((m: any) => m.id === (task as any).assigned_to)?.name || 'Unknown'}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <button
                        className="inline-flex items-center justify-center gap-2 min-h-[48px] px-5 rounded-full font-bold text-base bg-kid-card dark:bg-kid-card-dark border-2 border-kid-border-strong dark:border-kid-border-strong-dark text-kid-text dark:text-kid-text-dark disabled:opacity-50"
                        onClick={() => {
                          setEditTaskModal({ isOpen: true, task, isCreating: false });
                        }}
                      >
                        {i18n.t('admin_panel.edit')}
                      </button>
                      <button
                        className="inline-flex items-center justify-center gap-2 min-h-[48px] px-5 rounded-full font-bold text-base bg-transparent border-2 border-red-300 dark:border-red-800 text-red-800 dark:text-red-200 disabled:opacity-50"
                        onClick={() => setRemoveTaskModal({
                          isOpen: true,
                          taskId: task.id,
                          taskTitle: task.title
                        })}
                      >
                        {i18n.t('admin_panel.remove')}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              {(hasMoreCompletedTasks || hasCollapsedCompletedTasks) && (
                <div className="flex justify-center gap-3 pt-1">
                  {hasMoreCompletedTasks && (
                    <button
                      type="button"
                      className="inline-flex items-center justify-center gap-2 min-h-[48px] px-5 rounded-full font-bold text-base bg-kid-card dark:bg-kid-card-dark border-2 border-kid-border-strong dark:border-kid-border-strong-dark text-kid-text dark:text-kid-text-dark disabled:opacity-50"
                      disabled={loadingOlderCompleted}
                      onClick={async () => {
                        const next = completedTasksShown + COMPLETED_TASKS_PAGE_SIZE;
                        setCompletedTasksShown(next);
                        if (next > completedTasks.length && current_family.completed_has_more) {
                          setLoadingOlderCompleted(true);
                          try { await loadMoreCompletedTasks(); } finally { setLoadingOlderCompleted(false); }
                        }
                      }}
                    >
                      {i18n.t('admin_panel.show_more_completed_tasks')} ▼
                    </button>
                  )}
                  {hasCollapsedCompletedTasks && (
                    <button
                      type="button"
                      className="inline-flex items-center justify-center gap-2 min-h-[48px] px-5 rounded-full font-bold text-base bg-kid-card dark:bg-kid-card-dark border-2 border-kid-border-strong dark:border-kid-border-strong-dark text-kid-text dark:text-kid-text-dark disabled:opacity-50"
                      onClick={() => setCompletedTasksShown(COMPLETED_TASKS_INITIAL_COUNT)}
                    >
                      {i18n.t('admin_panel.show_less_completed_tasks')} ▲
                    </button>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8 text-kid-text-muted">
              <p>{i18n.t('admin_panel.no_completed_tasks')}</p>
            </div>
          )}
        </div>
      </div>
      {/* Remove Task Confirmation Modal */}
      {removeTaskModal.isOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[10050] p-4">
          <div className="bg-kid-card dark:bg-kid-card-dark rounded-kid-tile p-6 max-w-md w-full mx-4 border-2 border-kid-border dark:border-kid-border-dark">
            <div className="mb-4">
              <h3 className="font-kid-heading text-xl font-extrabold text-kid-text dark:text-kid-text-dark">{i18n.t('admin_panel.remove_task_title')}</h3>
            </div>
            <div className="mb-6">
              <p className="text-kid-text dark:text-kid-text-dark mb-2">{i18n.t('admin_panel.remove_task_prompt_prefix')} "<strong>{removeTaskModal.taskTitle}</strong>"?</p>
              {removeTaskModal.wholeGroup && <p className="text-kid-text dark:text-kid-text-dark mb-2 font-bold">{i18n.t('admin_panel.remove_shared_note')}</p>}
              <p className="text-kid-text dark:text-kid-text-dark">{i18n.t('admin_panel.irreversible_note')}</p>
            </div>
            <div className="flex gap-3">
              <button onClick={cancelRemoveTask} className="inline-flex items-center justify-center gap-2 min-h-[48px] px-5 rounded-full font-bold text-base bg-kid-card dark:bg-kid-card-dark border-2 border-kid-border-strong dark:border-kid-border-strong-dark text-kid-text dark:text-kid-text-dark disabled:opacity-50 flex-1">{i18n.t('admin_panel.cancel_btn')}</button>
              <button onClick={handleRemoveTask} className="inline-flex items-center justify-center gap-2 min-h-[48px] px-5 rounded-full font-bold text-base bg-transparent border-2 border-red-300 dark:border-red-800 text-red-800 dark:text-red-200 disabled:opacity-50 flex-1">{i18n.t('admin_panel.remove_task_btn')}</button>
            </div>
          </div>
        </div>
      )}
      {editTaskModal.isOpen && (
        <TaskFormModal
          task={editTaskModal.task}
          isCreating={editTaskModal.isCreating}
          onClose={() => setEditTaskModal({ isOpen: false, task: null, isCreating: false })}
        />
      )}
      </>
    );
};

export default TasksTab;
